"use client";

import { apiAuthFetch } from "@/lib/authFetch";

// ─── Types ──────────────────────────────────────────────────────────────────────

type LogLevel = "error" | "warn" | "info" | "debug";

type LogOptions = {
  error?: Error | unknown;
  component?: string;
  action?: string;
  metadata?: Record<string, unknown>;
};

type LogEntry = {
  level: LogLevel;
  message: string;
  error?: string | null;
  component?: string | null;
  action?: string | null;
  breadcrumbs: string[];
  metadata?: Record<string, unknown> | null;
};

// ─── Configuration ──────────────────────────────────────────────────────────────

const FLUSH_INTERVAL_MS = 5_000;
const MAX_QUEUE_SIZE = 100;
const MAX_BREADCRUMBS = 20;

// ─── State ──────────────────────────────────────────────────────────────────────

let queue: LogEntry[] = [];
let breadcrumbs: string[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let isFlushing = false;

// ─── Breadcrumbs ────────────────────────────────────────────────────────────────

function addBreadcrumb(label: string): void {
  if (typeof window === "undefined") return;
  breadcrumbs.push(`[${new Date().toISOString().slice(11, 23)}] ${label}`);
  if (breadcrumbs.length > MAX_BREADCRUMBS) {
    breadcrumbs = breadcrumbs.slice(-MAX_BREADCRUMBS);
  }
}

// ─── Core Logging ───────────────────────────────────────────────────────────────

function log(level: LogLevel, message: string, opts?: LogOptions): void {
  if (typeof window === "undefined") return;

  // Production telemetry surface = error only. Non-error levels stay in-process
  // (devtools console output is unaffected because this module never writes there).
  if (level !== "error" && import.meta.env.PROD) return;

  const errorStr = opts?.error
    ? opts.error instanceof Error
      ? `${opts.error.message}\n${opts.error.stack ?? ""}`
      : String(opts.error)
    : null;

  const entry: LogEntry = {
    level,
    message,
    error: errorStr,
    component: opts?.component ?? null,
    action: opts?.action ?? null,
    breadcrumbs: [...breadcrumbs],
    metadata: opts?.metadata ?? null,
  };

  queue.push(entry);

  // Cap queue to prevent unbounded growth
  if (queue.length > MAX_QUEUE_SIZE) {
    queue = queue.slice(-MAX_QUEUE_SIZE);
  }

  // Immediate flush on error
  if (level === "error") {
    void flush();
  }

  // Start periodic flush if not running
  ensureFlushTimer();
}

// ─── Flush ──────────────────────────────────────────────────────────────────────

async function flush(): Promise<void> {
  if (isFlushing || queue.length === 0) return;

  isFlushing = true;
  const batch = queue.splice(0, 20); // Take up to 20

  try {
    await apiAuthFetch("/api/telemetry", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entries: batch }),
    });
  } catch {
    // Put entries back at front of queue on failure (network down, 429, etc.)
    queue.unshift(...batch);
    if (queue.length > MAX_QUEUE_SIZE) {
      queue = queue.slice(-MAX_QUEUE_SIZE);
    }
  } finally {
    isFlushing = false;
  }
}

function ensureFlushTimer(): void {
  if (flushTimer !== null) return;
  flushTimer = setInterval(() => {
    void flush();
  }, FLUSH_INTERVAL_MS);
}

// ─── Public API ─────────────────────────────────────────────────────────────────

export const ClientLogger = {
  error: (message: string, opts?: LogOptions) => log("error", message, opts),
  warn: (message: string, opts?: LogOptions) => log("warn", message, opts),
  info: (message: string, opts?: LogOptions) => log("info", message, opts),
  debug: (message: string, opts?: LogOptions) => log("debug", message, opts),
  addBreadcrumb,
  flush,
};
